import './aiChat.css';

document.addEventListener('DOMContentLoaded', () => {
    const root = document.querySelector('#ai-chat');
    if (root) {
        new AiChat(root).init();
    }
});

class AiChat {
    constructor(root) {
        this.root = root;
        this.sendUrl = root.dataset.sendUrl;
        this.resetUrl = root.dataset.resetUrl;
        this.messages = root.querySelector('#ai-chat-messages');
        this.status = root.querySelector('#ai-chat-status');
        this.form = root.querySelector('#ai-chat-form');
        this.input = root.querySelector('#ai-chat-input');
        this.sendButton = root.querySelector('#ai-chat-send');
        this.resetButton = root.querySelector('#ai-chat-reset');
        this.busy = false;
    }

    init() {
        (window.aiChatTranscript || []).forEach((entry) => {
            if (entry.role === 'user') {
                this.addUserMessage(entry.text);
            } else {
                const bubble = this.addAssistantBubble();
                (entry.tools || []).forEach((t) => this.addToolLine(bubble, t.name, t.input, null));
                this.setAssistantText(bubble, entry.text || '');
                (entry.warnings || []).forEach((w) => this.addWarning(bubble, w));
                if (entry.usage) {
                    this.setUsage(bubble, entry.usage);
                }
            }
        });
        this.scrollToBottom();

        this.form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.send();
        });
        this.input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.send();
            }
        });
        this.resetButton.addEventListener('click', () => this.reset());
        this.input.focus();
    }

    async send() {
        const question = this.input.value.trim();
        if (question === '' || this.busy) {
            return;
        }
        this.setBusy(true);
        this.input.value = '';
        this.addUserMessage(question);
        const bubble = this.addAssistantBubble();
        this.setStatus('Přemýšlím…');
        this.scrollToBottom();

        try {
            const response = await fetch(this.sendUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body: JSON.stringify({ question }),
            });
            if (!response.ok) {
                throw new Error('HTTP ' + response.status);
            }
            const contentType = response.headers.get('content-type') || '';
            if (!contentType.includes('text/event-stream')) {
                const data = await response.json();
                throw new Error(data.error || 'Neočekávaná odpověď serveru.');
            }
            await this.consumeStream(response.body, bubble);
        } catch (err) {
            this.addWarning(bubble, 'Chyba: ' + err.message);
        } finally {
            this.setBusy(false);
            this.setStatus('');
            this.scrollToBottom();
            this.input.focus();
        }
    }

    async consumeStream(body, bubble) {
        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let text = '';
        for (;;) {
            const { value, done } = await reader.read();
            if (done) {
                break;
            }
            buffer += decoder.decode(value, { stream: true });
            let idx;
            while ((idx = buffer.indexOf('\n\n')) !== -1) {
                const frame = buffer.slice(0, idx);
                buffer = buffer.slice(idx + 2);
                const parsed = this.parseFrame(frame);
                if (!parsed) {
                    continue;
                }
                const { event, data } = parsed;
                switch (event) {
                    case 'tool_call':
                        this.setStatus('Volám nástroj ' + data.name + '…');
                        this.addToolLine(bubble, data.name, data.input, null);
                        break;
                    case 'tool_result':
                        this.setStatus('Zpracovávám výsledek ' + data.name + '…');
                        this.updateToolLine(bubble, data);
                        break;
                    case 'text':
                        text += (text === '' ? '' : '\n\n') + data.text;
                        this.setAssistantText(bubble, text);
                        break;
                    case 'warning':
                        this.addWarning(bubble, data.text);
                        break;
                    case 'usage':
                        this.setUsage(bubble, data);
                        break;
                    case 'error':
                        this.addWarning(bubble, 'Chyba: ' + data.text);
                        break;
                    case 'done':
                        break;
                }
                this.scrollToBottom();
            }
        }
        if (text === '' && !bubble.querySelector('.ai-chat__warning')) {
            this.addWarning(bubble, 'Model nevrátil žádný text.');
        }
    }

    parseFrame(frame) {
        let event = 'message';
        const dataLines = [];
        frame.split('\n').forEach((line) => {
            if (line.startsWith('event:')) {
                event = line.slice(6).trim();
            } else if (line.startsWith('data:')) {
                dataLines.push(line.slice(5).trim());
            }
        });
        if (dataLines.length === 0) {
            return null;
        }
        try {
            return { event, data: JSON.parse(dataLines.join('\n')) };
        } catch (e) {
            return null;
        }
    }

    async reset() {
        if (this.busy || !window.confirm('Smazat historii konverzace?')) {
            return;
        }
        await fetch(this.resetUrl, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        this.messages.innerHTML = '';
        this.input.focus();
    }

    // --- DOM helpers ---------------------------------------------------------

    addUserMessage(text) {
        const el = document.createElement('div');
        el.className = 'ai-chat__msg ai-chat__msg--user';
        el.innerHTML = '<div class="ai-chat__bubble">' + escapeHtml(text).replace(/\n/g, '<br>') + '</div>';
        this.messages.appendChild(el);
    }

    addAssistantBubble() {
        const el = document.createElement('div');
        el.className = 'ai-chat__msg ai-chat__msg--assistant';
        el.innerHTML = '<div class="ai-chat__bubble"><div class="ai-chat__tools"></div><div class="ai-chat__text"></div><div class="ai-chat__meta"></div></div>';
        this.messages.appendChild(el);
        return el;
    }

    addToolLine(bubble, name, input, result) {
        const line = document.createElement('div');
        line.className = 'ai-chat__tool';
        line.dataset.name = name;
        const args = input && Object.keys(input).length ? JSON.stringify(input) : '';
        line.innerHTML = '<i class="bi bi-tools"></i> <code>' + escapeHtml(name) + '</code> <span class="ai-chat__tool-args">' + escapeHtml(args) + '</span> <span class="ai-chat__tool-result"></span>';
        bubble.querySelector('.ai-chat__tools').appendChild(line);
        if (result) {
            this.updateToolLine(bubble, result);
        }
        return line;
    }

    updateToolLine(bubble, data) {
        const lines = bubble.querySelectorAll('.ai-chat__tool[data-name="' + CSS.escape(data.name) + '"]');
        const line = Array.from(lines).reverse().find((l) => !l.dataset.done) || lines[lines.length - 1];
        if (!line) {
            return;
        }
        line.dataset.done = '1';
        const res = line.querySelector('.ai-chat__tool-result');
        res.textContent = data.error ? 'chyba' : (data.chars + ' znaků');
        res.title = data.preview || '';
        if (data.error) {
            res.classList.add('text-danger');
        }
    }

    setAssistantText(bubble, text) {
        bubble.querySelector('.ai-chat__text').innerHTML = renderMarkdown(text);
    }

    addWarning(bubble, text) {
        const el = document.createElement('div');
        el.className = 'ai-chat__warning text-warning small';
        el.innerHTML = '<i class="bi bi-exclamation-triangle"></i> ' + escapeHtml(text);
        bubble.querySelector('.ai-chat__bubble').appendChild(el);
    }

    setUsage(bubble, usage) {
        const parts = [];
        parts.push('vstup ' + formatNumber(usage.input_tokens + (usage.cache_read_input_tokens || 0) + (usage.cache_creation_input_tokens || 0)));
        parts.push('výstup ' + formatNumber(usage.output_tokens));
        if (usage.cache_read_input_tokens) {
            parts.push('z cache ' + formatNumber(usage.cache_read_input_tokens));
        }
        bubble.querySelector('.ai-chat__meta').textContent = (usage.model || '') + ' · tokeny: ' + parts.join(', ');
    }

    setStatus(text) {
        this.status.textContent = text;
    }

    setBusy(busy) {
        this.busy = busy;
        this.sendButton.disabled = busy;
        this.resetButton.disabled = busy;
        this.input.disabled = busy;
        this.root.classList.toggle('ai-chat--busy', busy);
    }

    scrollToBottom() {
        this.messages.scrollTop = this.messages.scrollHeight;
    }
}

function escapeHtml(text) {
    return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function formatNumber(n) {
    return Number(n || 0).toLocaleString('cs-CZ');
}

/**
 * Minimal Markdown renderer for model answers: fenced code, headings, lists, tables,
 * bold/italic/inline code. Input is HTML-escaped first, so the output is safe.
 */
function renderMarkdown(md) {
    const lines = escapeHtml(md).split('\n');
    const out = [];
    let i = 0;
    while (i < lines.length) {
        let line = lines[i];

        if (line.startsWith('```')) {
            const code = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) {
                code.push(lines[i]);
                i++;
            }
            i++;
            out.push('<pre><code>' + code.join('\n') + '</code></pre>');
            continue;
        }

        if (/^\|.*\|\s*$/.test(line)) {
            const rows = [];
            while (i < lines.length && /^\|.*\|\s*$/.test(lines[i])) {
                rows.push(lines[i]);
                i++;
            }
            out.push(renderTable(rows));
            continue;
        }

        if (/^\s*([-*+]|\d+[.)])\s+/.test(line)) {
            const ordered = /^\s*\d+[.)]\s+/.test(line);
            const items = [];
            while (i < lines.length && /^\s*([-*+]|\d+[.)])\s+/.test(lines[i])) {
                items.push('<li>' + inline(lines[i].replace(/^\s*([-*+]|\d+[.)])\s+/, '')) + '</li>');
                i++;
            }
            out.push((ordered ? '<ol>' : '<ul>') + items.join('') + (ordered ? '</ol>' : '</ul>'));
            continue;
        }

        const heading = /^(#{1,6})\s+(.*)$/.exec(line);
        if (heading) {
            const level = Math.min(heading[1].length + 3, 6);
            out.push('<h' + level + '>' + inline(heading[2]) + '</h' + level + '>');
            i++;
            continue;
        }

        if (line.trim() === '') {
            i++;
            continue;
        }

        const para = [];
        while (i < lines.length && lines[i].trim() !== '' && !/^(```|\||#{1,6}\s|\s*([-*+]|\d+[.)])\s)/.test(lines[i])) {
            para.push(inline(lines[i]));
            i++;
        }
        out.push('<p>' + para.join('<br>') + '</p>');
    }
    return out.join('');
}

function renderTable(rows) {
    const cells = (row) => row.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
    const header = cells(rows[0]);
    let body = rows.slice(1);
    if (body.length && /^[\s|:-]+$/.test(body[0])) {
        body = body.slice(1);
    }
    let html = '<table class="table table-sm ai-chat__table"><thead><tr>' + header.map((h) => '<th>' + inline(h) + '</th>').join('') + '</tr></thead><tbody>';
    body.forEach((row) => {
        html += '<tr>' + cells(row).map((c) => '<td>' + inline(c) + '</td>').join('') + '</tr>';
    });
    return html + '</tbody></table>';
}

function inline(text) {
    return text
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
        .replace(/(^|[^*])\*([^*\n]+)\*(?!\*)/g, '$1<em>$2</em>');
}
